import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ActionType } from 'react-table';
import { v4 as uuid } from 'uuid';

import { getSearchableFields } from '~/components/SearchV2/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import BaseLoadView from '~/components/Shared/BaseLoadView/BaseLoadView';
import BaseSaveView from '~/components/Shared/BaseSaveView/BaseSaveView';
import { EntityVisibilityType } from '~/components/Shared/EntityVisibilitySelect/types';
import SearchV3BooleanSearchBar from '~/components/Shared/SearchV3BooleanSearchBar/SearchV3BooleanSearchBar';
import { Box } from '~/eds/src/components/Box/Box';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { TableContextType } from '~/enums/TableContextType';
import { TableViewContextType } from '~/enums/TableViewType';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { Filters } from '~/features/advanced-search/filters/Filters';
import { QueryBuilder } from '~/features/advanced-search/query-builder/QueryBuilder';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { ReadOnlyComplexQuery } from '~/features/advanced-search/read-only-complex-query/ReadOnlyComplexQuery';
import { SearchQuery } from '~/features/advanced-search/types';
import {
  buildComplexQueryWithCrossFilters,
  buildQuery,
  testIsFilterEmpty,
} from '~/features/advanced-search/utils/build-query';
import { getEmptyFilters } from '~/features/advanced-search/utils/filters';
import {
  DEFAULT_SEARCH_ID,
  getDefaultChatContext,
} from '~/features/ask-anything/ask-anything-button/utils';
import { useFieldsWithLazyLoad } from '~/features/filters/useFieldsWithLazyLoad';
import { SaveActionButton } from '~/features/search/SaveActionButton/SaveActionButton';
import { SaveEditEntityModal } from '~/features/search/SaveEditEntityModal/SaveEditEntityModal';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePinnedFilters as usePinnedFiltersDeprecated } from '~/hooks/usePinnedFilters_DEPRECATED';
import { useRecentSearches } from '~/hooks/useRecentSearches';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import {
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
} from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { DEFAULT_TABLE_SETTINGS } from '~/utils/table';
import { testIsAdmin } from '~/utils/user';

import { AddSearchResultsToGroup } from '../AddSearchResultsToGroup/AddSearchResultsToGroup';
import { useDocumentsBulkActionsPanel } from '../useDocumentsBulkActionsPanel';
import {
  testHasMixedOperatorInSection,
  testHasNestedSections,
  testIsOperator,
  testIsSection,
} from '../utils/queryUtils';
import { SearchV3Results } from './SearchV3Results';

export const SearchV3 = () => {
  const { navigate, params } = useRouting();
  const { searchId = '' } = params;
  const currentUser = useCurrentUser();
  const intl = useIntl();

  const { data: searchFilters, isFetching: isFetchingFilters } =
    api.endpoints.getFilters.useQuery(undefined);

  const {
    data: savedSearchResponse,
    isLoading: isLoadingSavedSearch,
    error: savedSearchError,
  } = api.endpoints.getSavedSearch.useQuery(searchId, { skip: !searchId });

  const { pushRecentSearch } = useRecentSearches('SEARCH');

  const { toast } = useToast();
  const dispatch = useDispatch();

  const [fetchTableView, tableViewResult] =
    api.endpoints.getCurrentTableView.useLazyQuery();
  const { data: currentTableView, isFetching: isFetchingCurrentTableViewSet } =
    tableViewResult;

  const [updateTableView] = api.endpoints.updateTableView.useMutation();
  const isQueryFiltersEnabled = useFlag(
    FlagType.QueryFiltersIntoSearchAndDashboard,
  );
  const hasTicketGroups = useFlag(FlagType.GroupedTicketsAndDocs);
  const activeFilters = useSelector(
    slices.searchV3.selectors.selectActiveFilters,
  );
  const page = useSelector(slices.searchV3.selectors.selectPage);
  const pageSize = useSelector(slices.searchV3.selectors.selectPageSize);
  const sortBy = useSelector(slices.searchV3.selectors.selectSortBy);
  const searchText = useSelector(slices.searchV3.selectors.selectSearchText);
  const columnOrder = useSelector(slices.searchV3.selectors.selectColumnOrder);
  const columnWidths = useSelector(
    slices.searchV3.selectors.selectColumnWidths,
  );
  const defaultFilters = useSelector(
    slices.searchV3.selectors.selectDefaultFilters,
  );
  const selectedFilters = useSelector(
    slices.searchV3.selectors.selectSelectedFilters,
  );
  const isSearchInitialized = useSelector(
    slices.searchV3.selectors.selectIsInitialized,
  );
  const featuredClauses = useSelector(
    slices.searchV3.selectors.selectFeaturedClauses,
  );
  const queryBuilder = useSelector(
    slices.searchV3.selectors.selectQueryBuilder,
  );
  const queryFilters = useSelector(
    slices.searchV3.selectors.selectQueryFilters,
  );
  const queryBuilderPanelVisible = useSelector(
    slices.searchV3.selectors.selectQueryBuilderPanelVisible,
  );
  const resultsCache = useSelector(
    slices.searchV3.selectors.selectResultsCache,
  );
  const savedSearch = useSelector(slices.searchV3.selectors.selectSavedSearch);
  const isDirty = useSelector(slices.searchV3.selectors.selectIsSearchDirty);
  const [isSaveAsVisible, setIsSaveAsVisible] = useState(false);
  const shouldPerformSearch =
    isSearchInitialized ||
    activeFilters.length > 0 ||
    queryBuilder ||
    (isQueryFiltersEnabled && queryFilters);

  const hasSavePermission =
    testIsAdmin(currentUser) || savedSearch?.userId === currentUser.id;
  const isSaveDisabled = !isDirty || !hasSavePermission;

  // local state for optimistic update
  const [localPinnedFilterIds, setLocalPinnedFilterIds] = useState<
    Array<PilotId | string>
  >([]);
  const [isAddToGroupVisible, setIsAddToGroupVisible] = useState(false);

  const [isLoadViewVisible, setIsLoadViewVisible] = useState(false);
  const [isSaveViewVisible, setIsSaveViewVisible] = useState(false);

  const {
    getPinnedFilters,
    result: {
      data: userPreferencesDefaultFilters,
      isFetching: isFetchingPinnedFilters,
    },
  } = usePinnedFiltersDeprecated(searchFilters);
  const [updatePinnedFilters] = api.endpoints.updatePinnedFilters.useMutation();
  const [postSavedSearch] = api.endpoints.postSavedSearch.useMutation();
  const [putSavedSearch] = api.endpoints.putSavedSearch.useMutation();

  useEffect(() => {
    if (savedSearchResponse) {
      dispatch(slices.searchV3.actions.setSavedSearch(savedSearchResponse));
    }
    return () => {
      dispatch(slices.searchV3.actions.setSavedSearch(undefined));
    };
  }, [savedSearchResponse]);

  useEffect(() => {
    if (searchFilters) {
      if (savedSearch) {
        dispatch(
          slices.searchV3.actions.initSavedSearchData({
            searchFilters,
            pageSize: tableSettings.pageSize,
            sortBy: tableSettings.sortBy,
          }),
        );
      } else {
        const defaultColumnOrder = currentTableView?.map((field) =>
          String(field.field_id),
        );
        dispatch(
          slices.searchV3.actions.init({
            columnOrder: defaultColumnOrder ?? [],
            selectedFilters: defaultFilters,
            pageSize: tableSettings.pageSize,
            sortBy: tableSettings.sortBy,
          }),
        );
      }
    }
  }, [savedSearch, searchFilters, currentTableView]);

  const query = useMemo(() => {
    const shouldUseQueryFilters = queryFilters && isQueryFiltersEnabled;
    if ((shouldUseQueryFilters || queryBuilder) && searchFilters) {
      const queryToUse = shouldUseQueryFilters
        ? isActiveQuery(queryFilters)
        : queryBuilder;
      return buildComplexQueryWithCrossFilters(
        queryToUse ?? [],
        [],
        searchFilters,
        searchText,
      );
    } else if (activeFilters?.length || searchFilters) {
      return buildQuery({
        booleanQuery: searchText ?? '',
        filters: activeFilters,
        searchFilters,
      });
    } else {
      return [];
    }
  }, [activeFilters, searchFilters, searchText, queryBuilder, queryFilters]);
  const queryRef = useRef(query);

  const {
    data: searchV3Data,
    isFetching: isFetchingSearchResults,
    isError: isErrorSearchDocuments,
    refetch: refetchSearchV3Documents,
  } = api.endpoints.searchV3Documents.useQuery(
    {
      query,
      page,
      pageSize,
      sortBy,
      filters: columnOrder?.filter((column) => column !== 'name'),
      store_as_recent_search: false,
    },
    { skip: !shouldPerformSearch },
  );
  const handleSaveAs = async (
    data: { name: string } & EntityVisibilityType,
  ) => {
    try {
      const response = await postSavedSearch({
        name: data.name,
        visibility: data.visibility,
        userIds: data.userIds,
        departmentIds: data.departmentIds,
        query: query,
        filters: isQueryFiltersEnabled
          ? getEmptyFilters(queryFilters ?? []).map((f) => f.id.toString())
          : selectedFilters
              .filter(testIsFilterEmpty)
              .map((filter) => filter.fieldId),
        resultTable: columnOrder
          ?.filter((column) => column !== 'name')
          .map((column, index) => ({
            filterId: column,
            columnIndex: index,
          })),
      }).unwrap();
      if (response) {
        dispatch(slices.searchV3.actions.setSearchText(''));
        toast({
          status: 'success',
          message: intl.formatMessage(
            {
              id: 'SearchV3.searchSavedMessage',
              defaultMessage: 'Search **{searchName}** has been saved.',
              description:
                'Success message when search is saved. Example: searchName = "My Contract Search"',
            },
            { searchName: response.name },
          ),
        });
        navigate(`/search/${response.id}`);
        setIsSaveAsVisible(false);
      }
    } catch (error: any) {
      toast({
        status: 'danger',
        message: error.message,
      });
    }
  };

  const handleSave = async () => {
    if (savedSearch) {
      try {
        const response = await putSavedSearch({
          id: savedSearch.id,
          savedSearch: {
            name: savedSearch.name,
            visibility: savedSearch.visibility,
            userIds: savedSearch.userIds,
            departmentIds: savedSearch.departmentIds,
            query: query,
            filters: isQueryFiltersEnabled
              ? getEmptyFilters(queryFilters ?? []).map((f) => f.id.toString())
              : selectedFilters
                  .filter(testIsFilterEmpty)
                  .map((filter) => filter.fieldId),
            resultTable: columnOrder
              ?.filter((column) => column !== 'name')
              .map((column, index) => ({
                filterId: column,
                columnIndex: index,
              })),
          },
        }).unwrap();
        if (response) {
          toast({
            status: 'success',
            message: intl.formatMessage(
              {
                id: 'SearchV3.searchSavedMessage2',
                defaultMessage: 'Search **{searchName}** has been saved.',
                description:
                  'Success message when search is saved. Example: searchName = "My Contract Search"',
              },
              { searchName: response.name },
            ),
          });
        }
      } catch (error: any) {
        toast({
          status: 'danger',
          message: error.message,
        });
      }
    }
  };

  const {
    tableSettings = {
      ...DEFAULT_TABLE_SETTINGS,
      columnOrder: (savedSearch?.resultTable ?? []).map((column) =>
        String(column.filterId),
      ),
    },
    updateTableSettings,
  } = useTableSettings(TableContextType.SearchV3);

  const pinnedFiltersIds = useMemo(() => {
    return userPreferencesDefaultFilters?.map((filter) =>
      isNaN(Number(filter.fieldId)) ? filter.fieldId : Number(filter.fieldId),
    );
  }, [userPreferencesDefaultFilters]);

  const refreshSearchResult = useCallback(() => {
    resetSearchResultSelection();
    refetchSearchV3Documents();
  }, []);

  const resetSearchResultSelection = () => {
    tableSelection.clear();
  };

  const tableSelection = useTableSelection();
  const { panel, limitExceededModal, setActivePanel } =
    useDocumentsBulkActionsPanel({
      selectedDocumentIds: tableSelection?.selectedIds,
      isAllSelected: tableSelection?.isAllSelected,
      totalCount: searchV3Data?.meta?.total ?? 0,
      query: query,
      searchAPI: 'v3',
      onActionCompleted: refreshSearchResult,
      resultsCache: resultsCache,
    });

  const onSearch = (search: string) => {
    dispatch(slices.searchV3.actions.setSearchText(search));
    dispatch(slices.searchV3.actions.setPage(1));
    if (search) {
      trackSegment('searchKeywords', {
        name: search,
      });
    }
  };

  const onSetColumnOrder = useCallback(
    (updatedColumnOrder: Array<string>) => {
      dispatch(slices.searchV3.actions.setColumnOrder(updatedColumnOrder));
      updateTableSettings({
        ...tableSettings,
        columnOrder: updatedColumnOrder,
      });
      if (!savedSearch) {
        updateTableView({
          context: TableViewContextType.SEARCH,
          fields: getSearchableFields(updatedColumnOrder),
        });
      }

      const queryFields = updatedColumnOrder
        .map((columnId) => (searchFilters ?? {})[columnId])
        .filter((field) => field);
      dispatch(slices.searchV3.actions.setQueryFields(queryFields));
    },
    [searchFilters, tableSettings, savedSearch],
  );

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        dispatch(slices.searchV3.actions.setPage(pageIndex));
      }
    },
    [page],
  );

  const onPageSizeChange = useCallback((pageSize: number) => {
    dispatch(slices.searchV3.actions.setPageSize(pageSize));
  }, []);

  const handleUpdate = (state: any, action?: ActionType) => {
    tableSelection.onTableUpdate(state, action);
    if (action?.type === 'columnDoneResizing') {
      dispatch(
        slices.searchV3.actions.patchColumnWidths(
          state.columnResizing.columnWidths,
        ),
      );
    }
    if (action?.type === 'toggleSortBy') {
      dispatch(slices.searchV3.actions.setSortBy(state.sortBy[0]));
    }
  };
  const handleUpdatePinnedFilters = async (
    updatedPinnedFilters: Array<PilotId | string>,
  ) => {
    setLocalPinnedFilterIds(updatedPinnedFilters);
    await updatePinnedFilters({
      context: 'SEARCH',
      pinnedFilters: updatedPinnedFilters as string[],
    });
    getPinnedFilters('SEARCH');
  };

  const openQueryBuilderPanel = () => {
    dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(true));
  };
  const hideQueryBuilderPanel = () => {
    dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(false));
  };

  const onFiltersReset = () => {
    if (searchFilters) {
      dispatch(
        slices.searchV3.actions.initSavedSearchData({
          searchFilters,
          pageSize: tableSettings.pageSize,
          sortBy: tableSettings.sortBy,
        }),
      );
      dispatch(slices.searchV3.actions.setPage(1));
    }
  };

  const handleSearchQueryBuilder = (queryBuilderPayload: SearchQuery) => {
    dispatch(slices.searchV3.actions.setQueryBuilder(queryBuilderPayload));
    dispatch(slices.searchV3.actions.setPage(1));
    dispatch(slices.searchV3.actions.setQueryBuilderPanelVisible(false));
    dispatch(slices.searchV3.actions.setSearchText(''));
  };

  // Viewset
  const showSaveView = useCallback(() => {
    setIsSaveViewVisible(true);
  }, []);

  const showLoadView = useCallback(() => {
    setIsLoadViewVisible(true);
  }, []);

  // Bulk Actions
  const handleMove = useCallback(() => {
    setActivePanel('move');
  }, []);

  const handleCopy = useCallback(() => {
    setActivePanel('copy');
  }, []);

  const handleEdit = useCallback(() => {
    setActivePanel('edit');
  }, []);

  const handleEditClauses = useCallback(() => {
    setActivePanel('edit-clauses');
  }, []);

  const handleDelete = useCallback(() => {
    setActivePanel('delete');
  }, []);

  const handleSuccessAddToGroup = useCallback(() => {
    refreshSearchResult();
    setIsAddToGroupVisible(false);
  }, []);

  useEffect(() => {
    if (!searchId) {
      getPinnedFilters('SEARCH')
        .unwrap()
        .then((response) => {
          if (!response) {
            return;
          }
          const { data } = response;
          const pinnedFilters = data?.map((fieldId) => ({
            id: uuid(),
            fieldId: (Number(fieldId) || fieldId) as FieldId,
            operatorId: null,
            values: [],
          }));
          dispatch(slices.searchV3.actions.setDefaultFilters(pinnedFilters));
          if (selectedFilters.length === 0) {
            dispatch(
              slices.searchV3.actions.setSelectedFilters(pinnedFilters ?? []),
            );
          }
        });
    } else {
      getPinnedFilters('SEARCH');
    }

    dispatch(
      slices.chatbot.actions.setContext([
        { ...getDefaultChatContext(intl), primary: true },
      ]),
    );
  }, []);

  useEffect(() => {
    if (pinnedFiltersIds && pinnedFiltersIds.length) {
      setLocalPinnedFilterIds(pinnedFiltersIds ?? []);
    }
  }, [pinnedFiltersIds]);

  useEffect(() => {
    if (!!isErrorSearchDocuments) {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'SearchV3.searchDocumentsError',
          defaultMessage: 'An error occurred while searching for documents.',
          description: 'Error message when document search fails',
        }),
      });
    }
  }, [isErrorSearchDocuments]);

  useEffect(() => {
    if (searchV3Data?.results) {
      queryRef.current = query;
      dispatch(
        slices.searchV3.actions.patchSearchResultsCache(searchV3Data.results),
      );
      if (searchV3Data.results.length > 0) {
        dispatch(
          slices.chatbot.actions.setContext([
            getDefaultChatContext(intl),
            {
              label: intl.formatMessage({
                id: 'SearchV3.allResultsLabel',
                defaultMessage: 'All Results',
                description: 'Label for all search results context',
              }),
              query,
              title: intl.formatMessage(
                {
                  id: 'SearchV3.allResultsTitle',
                  defaultMessage: 'All Results ({total})',
                  description:
                    'Title showing all results with total count. Example: total = 150',
                },
                {
                  total: searchV3Data.meta.total,
                },
              ),
              icon: 'search',
              primary: true,
              searchEntity: 'document',
              selectedIds: [],
              id: DEFAULT_SEARCH_ID,
              type: 'all_results',
            },
          ]),
        );
      }
    }
  }, [searchV3Data]);

  // Fetch Columns
  useEffect(() => {
    if (!searchId) {
      fetchTableView({ context: TableViewContextType.SEARCH });
    } else if (savedSearch) {
      const savedSearchColumns = savedSearch.resultTable.map((column) =>
        String(column.filterId),
      );
      onSetColumnOrder(savedSearchColumns);
    }
  }, [searchId, savedSearch]);

  useEffect(() => {
    return () => {
      pushRecentSearch({
        query: queryRef.current,
        datetime: Date.now(),
      });
    };
  }, []);

  const isQueryBuilderSupported = useMemo(() => {
    if (!queryBuilder || savedSearch?.isComplexQuery === false) return true;
    return (
      !testHasMixedOperatorInSection(queryBuilder) &&
      !testHasNestedSections(queryBuilder) &&
      queryBuilder?.every(
        (queryEntity) =>
          testIsSection(queryEntity) || testIsOperator(queryEntity),
      )
    );
  }, [savedSearch, queryBuilder]);

  const isLoading =
    isFetchingFilters ||
    isFetchingSearchResults ||
    isFetchingCurrentTableViewSet ||
    isFetchingPinnedFilters;

  const onFilterChange = (filters: Filter[]) => {
    dispatch(slices.searchV3.actions.setSelectedFilters(filters));
    dispatch(slices.searchV3.actions.setPage(1));
  };

  const loadingContent = {
    isLoading: isLoadingSavedSearch,
  };

  const getTitle = () => {
    if (!searchId)
      return intl.formatMessage({
        id: 'SearchV3.searchTitle',
        defaultMessage: 'Search',
        description: 'Default title for search page',
      });
    return savedSearch ? savedSearch.name : '';
  };

  const placeholderContent = savedSearchError
    ? {
        title: intl.formatMessage({
          id: 'SearchV3.noAccessTitle',
          defaultMessage: 'No Access to Page',
          description: 'Title for no access error page',
        }),
        message: intl.formatMessage({
          id: 'SearchV3.noAccessMessage',
          defaultMessage:
            'The saved search does not exist or you do not have permission to access this saved search. To request permission, contact the user who created the saved search.',
          description: 'Message explaining no access to saved search',
        }),
        image: 'error-page-access',
        action: {
          text: intl.formatMessage({
            id: 'SearchV3.takeHomeButton',
            defaultMessage: 'Take me Home',
            description: 'Button text to navigate to home page',
          }),
          level: 'primary' as const,
          onClick: () => navigate(RoutePathType.Home),
        },
      }
    : undefined;

  const searchFiltersArray = useMemo(
    () => Object.values(searchFilters ?? {}),
    [searchFilters],
  );

  const searchFiltersList = useFieldsWithLazyLoad({
    filters: searchFiltersArray,
  });

  return (
    <>
      <PageLayout
        loadingContent={loadingContent}
        placeholderContent={placeholderContent}
        title={getTitle()}
      >
        <Layout direction="column" spacing={4}>
          <Layout align="flex-start" w="100%">
            <Layout direction="column" flex={1} mr={2}>
              <SearchV3BooleanSearchBar
                value={searchText ?? ''}
                onSubmit={onSearch}
                isSubmitting={isLoading}
              />
            </Layout>
            <Box>
              <SaveActionButton
                entity="Search"
                saveAsNewAction={{
                  disabled: !searchId && !isDirty,
                  onClick: () => setIsSaveAsVisible(true),
                }}
                overwriteAction={
                  searchId
                    ? {
                        onClick: handleSave,
                        tooltip: hasSavePermission
                          ? ''
                          : intl.formatMessage({
                              id: 'SearchV3.noSavePrivilegesMessage',
                              defaultMessage:
                                'You do not have privileges to save this search.',
                              description:
                                'Tooltip message when user lacks save privileges',
                            }),
                        disabled: isSaveDisabled,
                      }
                    : undefined
                }
              />
            </Box>
          </Layout>
          {isQueryFiltersEnabled ? (
            <QueryFilters
              enablePinnedFilters
              query={queryFilters ?? []}
              defaultFilters={selectedFilters.map((a) => a.fieldId)}
              onReset={savedSearch ? onFiltersReset : undefined}
              onQueryChange={(updatedQuery) => {
                dispatch(slices.searchV3.actions.setQueryFilters(updatedQuery));
              }}
            />
          ) : (
            <Box>
              {queryBuilder ? (
                <>
                  {!isFetchingFilters && (
                    <ReadOnlyComplexQuery
                      queryBuilder={queryBuilder}
                      isQueryBuilderSupported={isQueryBuilderSupported}
                      searchFilters={searchFilters || {}}
                      onClear={() => {
                        dispatch(
                          slices.searchV3.actions.setQueryBuilder(undefined),
                        );
                        dispatch(
                          slices.searchV3.actions.setQueryBuilderPanelVisible(
                            false,
                          ),
                        );
                      }}
                      onEdit={() => {
                        dispatch(
                          slices.searchV3.actions.setQueryBuilderPanelVisible(
                            true,
                          ),
                        );
                      }}
                      onOpenQueryBuilder={openQueryBuilderPanel}
                    />
                  )}
                </>
              ) : (
                <Layout direction="column">
                  <>
                    {isFetchingFilters ? (
                      <LoadingShimmer height="filter.height" />
                    ) : (
                      <Filters
                        searchFilters={searchFiltersList}
                        selectedFilters={selectedFilters}
                        pinnedFiltersIds={localPinnedFilterIds}
                        onChange={onFilterChange}
                        onFiltersReset={
                          savedSearch ? onFiltersReset : undefined
                        }
                        onQueryButtonClick={openQueryBuilderPanel}
                        onUpdatePinnedFilters={handleUpdatePinnedFilters}
                      />
                    )}
                  </>
                </Layout>
              )}
            </Box>
          )}

          <Box mt={2}>
            {shouldPerformSearch || isFetchingSearchResults ? (
              <SearchV3Results
                currentTableView={currentTableView}
                state={{
                  page,
                  pageSize,
                  sortBy,
                  columnOrder,
                  columnWidths,
                  featuredClauses,
                }}
                searchFilters={searchFilters || {}}
                isLoading={isFetchingSearchResults}
                data={searchV3Data}
                query={query}
                refreshSearch={refreshSearchResult}
                onSetColumnOrder={onSetColumnOrder}
                onPaginate={onPaginate}
                onPageSizeChange={onPageSizeChange}
                onMove={handleMove}
                onCopy={handleCopy}
                onEdit={handleEdit}
                onEditClauses={handleEditClauses}
                onDelete={handleDelete}
                onAddToGroup={() => setIsAddToGroupVisible(true)}
                resultsCache={resultsCache}
                tableSelection={tableSelection}
                handleUpdate={handleUpdate}
                onSaveView={showSaveView}
                onLoadView={showLoadView}
              />
            ) : (
              <EmptyPage preset="no-search" />
            )}
          </Box>
        </Layout>
      </PageLayout>
      {!!searchFilters &&
        isQueryBuilderSupported &&
        queryBuilderPanelVisible && (
          <QueryBuilder
            pinnedFiltersIds={localPinnedFilterIds}
            query={queryBuilder}
            searchFilters={searchFiltersList}
            onQueryChange={handleSearchQueryBuilder}
            onHide={hideQueryBuilderPanel}
          />
        )}
      {limitExceededModal}
      {panel}
      <SaveEditEntityModal
        placeholder={intl.formatMessage({
          id: 'SearchV3.savedSearchPlaceholder',
          defaultMessage: 'Saved Search Name',
          description: 'Placeholder text for saved search name input',
        })}
        onSubmit={handleSaveAs}
        onHide={() => setIsSaveAsVisible(false)}
        isVisible={isSaveAsVisible}
        title={intl.formatMessage({
          id: 'SearchV3.saveAsNewSearchTitle',
          defaultMessage: 'Save as New Search',
          description: 'Title for save as new search modal',
        })}
      />
      {!hasTicketGroups && isAddToGroupVisible && (
        <AddSearchResultsToGroup
          query={query}
          tableSelection={tableSelection}
          onHide={() => setIsAddToGroupVisible(false)}
          onCompleted={handleSuccessAddToGroup}
        />
      )}

      {isSaveViewVisible && (
        <BaseSaveView
          context={TableViewContextType.SEARCH}
          fields={getSearchableFields(columnOrder)}
          hideModal={() => setIsSaveViewVisible(false)}
          modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE}
        />
      )}

      {isLoadViewVisible && (
        <BaseLoadView
          context={TableViewContextType.SEARCH}
          modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH}
          hideModal={() => setIsLoadViewVisible(false)}
          onViewSelected={() =>
            fetchTableView({ context: TableViewContextType.SEARCH })
          }
        />
      )}
    </>
  );
};
